import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  CalendarDays, 
  Users, 
  Trophy,
  User,
  Info,
  TrendingUp,
  Activity
} from "lucide-react";
import { Modal, Spinner, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getHistoryBookingsAPI } from "../../services/bookingServices";
import { sportTypeEnum } from "../../App"; 
import "../../assets/styles/AdminBooking.css"; // 🔥 الستايل الجديد
import "../../assets/styles/StudentBookingModern.css"; // عشان المودال

export default function AdminBookingHistory() {
  // --- State ---
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSport, setSelectedSport] = useState(sportTypeEnum.football);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // --- Fetching Data ---
  const { data: bookings = [], isLoading, isError } = useQuery({
    queryKey: ["adminHistory", selectedDate, selectedSport],
    queryFn: async () => {
      const res = await getHistoryBookingsAPI({ date: selectedDate, sportType: selectedSport });
      return res.bookings || [];
    },
    keepPreviousData: true, 
  });

  // --- Helper: Count Players ---
  const totalPlayers = bookings.reduce((acc, curr) => acc + (curr.members?.length || 0), 0);

  return (
    <div style={{marginTop:"60px"}} className="admin-history-container pt-5">
      <div className="container">
        
        {/* 1. Header & Stats Section */}
        <div className="row g-4 mb-5">
          <div className="col-12 col-md-4">
            <div className="stats-card">
              <div className="stats-icon-box blue">
                <Trophy />
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Total Bookings</div>
                <h3 className="m-0 fw-bold">{bookings.length}</h3>
              </div>
            </div>
          </div>
          
          <div className="col-12 col-md-4">
            <div className="stats-card">
              <div className="stats-icon-box green">
                <Users />
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Active Players</div>
                <h3 className="m-0 fw-bold">{totalPlayers}</h3>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="stats-card">
              <div className="stats-icon-box orange">
                <Activity />
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Availability</div>
                <h3 className="m-0 fw-bold">{Math.max(0, 6 - bookings.length)} Slots Left</h3>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Filters Bar (Date & Tabs) */}
        <div className="filters-bar">
          <div className="d-flex align-items-center gap-2">
            <CalendarDays size={20} className="text-primary" />
            <input 
              type="date" 
              className="custom-date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          <div className="d-flex gap-2 ms-md-auto overflow-auto">
            {[sportTypeEnum.football, sportTypeEnum.basketball, sportTypeEnum.volleyball].map((sport) => (
              <button
                key={sport}
                className={`sport-tab ${selectedSport === sport ? 'active' : ''}`}
                onClick={() => setSelectedSport(sport)}
              >
                {sport}
              </button>
            ))}
          </div>
        </div>

        {/* 3. Timeline Content */}
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Fetching timeline...</p>
              </div>
            ) : isError ? (
              <div className="alert alert-danger text-center shadow-sm border-0">Failed to load data.</div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-5">
                <div className="bg-white d-inline-block p-4 rounded-circle shadow-sm mb-3">
                  <TrendingUp size={40} className="text-muted opacity-50" />
                </div>
                <h5 className="text-muted">No bookings for this day</h5>
                <p className="text-secondary small">Switch date or sport to see history.</p>
              </div>
            ) : (
              <div className="timeline-wrapper">
                <div className="timeline-line"></div>
                
                {bookings.map((booking, index) => (
                  <div key={booking._id || index} className="timeline-item">
                    <div className="timeline-dot"></div>
                    
                    <div 
                      className="booking-card-modern"
                      onClick={() => {
                        setSelectedTeam(booking);
                        setShowModal(true);
                      }}
                    >
                      {/* Left: Time & Slot */}
                      <div className="d-flex align-items-center gap-4">
                        <div className="text-center" style={{minWidth: '80px'}}>
                          <div className="booking-time">{booking.slot.split(' - ')[0]}</div>
                          <div className="small text-muted">{booking.slot.split(' - ')[1]}</div>
                        </div>
                        
                        <div>
                          <h5 className="fw-bold mb-1 text-dark">{booking.teamName}</h5>
                          <div className="d-flex align-items-center gap-2 text-secondary small">
                            <span className={`booking-status status-${booking.status}`}>
                              {booking.status}
                            </span>
                            <span>• {booking.members?.length} Players</span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Leader Avatar */}
                      <div className="d-none d-md-flex align-items-center gap-3">
                        <div className="text-end">
                          <div className="fw-bold small text-dark">Team Leader</div>
                          <div className="small text-muted">{booking.members?.[0]?.fullName || "Unknown"}</div>
                        </div>
                        {booking.members?.[0]?.profileImage ? (
                          <img 
                            src={booking.members[0].profileImage} 
                            alt="Leader" 
                            className="team-leader-avatar" 
                          />
                        ) : (
                          <div className="team-leader-avatar bg-light d-flex align-items-center justify-content-center text-secondary">
                            <User size={20} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* 4. The Pro Modal (Standard Reusable) */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="pro-modal"
      >
        <Modal.Header closeButton closeVariant="white" className="pro-modal-header">
          <Modal.Title className="pro-modal-title d-flex align-items-center">
            <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
              <CalendarDays size={20} className="text-white" />
            </div>
            <div>
              <div style={{ fontSize: "0.8rem", opacity: 0.8, fontWeight: "normal" }}>
                Team Details
              </div>
              {selectedTeam?.teamName || "Unknown Team"}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pro-modal-body">
          <div className="d-flex flex-column">
              {selectedTeam?.members && selectedTeam.members.length > 0 ? (
                selectedTeam.members.map((member, idx) => {
                  const isCaptain = idx === 0;
                  return (
                    <div key={member._id || idx} className="player-card">
                      <div className="d-flex align-items-center">
                        <div className="position-relative">
                          {member.profileImage ? (
                            <img src={member.profileImage} alt={member.fullName} className={`player-avatar ${isCaptain ? "is-captain-border" : ""}`} />
                          ) : (
                            <div className={`player-avatar d-flex align-items-center justify-content-center bg-light text-secondary ${isCaptain ? "is-captain-border" : ""}`}>
                                <User size={24} />
                            </div>
                          )}
                          
                          {isCaptain && (
                            <div className="position-absolute start-50 translate-middle-x" style={{top: '-12px', color: '#ffc107', filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))'}}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M11.668 1.564a2 2 0 1 1 3.284 2.822 2 2 0 0 1-2.924.976l-1.928 6.587A.5.5 0 0 1 9.605 12H6.395a.5.5 0 0 1-.495-.415l-1.928-6.587a2 2 0 0 1-2.924-.976 2 2 0 1 1 3.284-2.822l2.365 3.313 2.91-4.949z"/>
                                </svg>
                            </div>
                          )}
                        </div>

                        <div className="ms-3 player-info">
                          <div className="d-flex align-items-center">
                            <h6>{member.fullName || "Unknown Player"}</h6>
                            {isCaptain && <span className="captain-badge">Captain</span>}
                          </div>
                          <span className="text-uppercase">{member.universtyID}</span>
                        </div>
                      </div>

                      <Link to={`/admin/view-user-profile/${member._id}`} className="btn-view-profile text-decoration-none">
                        Profile
                      </Link>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-muted py-4">
                  <Info size={30} className="mb-2 opacity-50"/>
                  <p>No members data available.</p>
                </div>
              )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}